import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  tableCellClasses,
  Typography,
  Button,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import Page from '../../components/Page';
import BrudCrumbs from '../../components/BreadCrumbs';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import UserMoreMenu from '../../components/_dashboard/user/UserMoreMenu';
import partnerService from '../services/partnerService';
import OnBoardingToolbar from './OnBoardingToolbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getOnboardingsList } from '../../store/Slice/Onboarding/OnboardingSlice';
import useOnboardingSelector from '../../store/Slice/Onboarding/OnboardingSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useDebounce from '../../utils/useDebounce';
import { NOT_AVAILABLE } from '../Global/Constants';

const { UpdatePartnerStatus, PartnerStatusTypes } = partnerService;

const TABLE_HEAD = [
  { id: 'id', label: 'PARTNER ID', alignRight: false },
  { id: 'partnerName', label: 'PARTNER NAME', alignRight: false },
  { id: 'partnerType', label: 'PARTNER TYPE', alignRight: false },
  { id: 'city', label: 'LOCATION', alignRight: false },
  { id: 'randomPhoneNumber', label: 'MOBILE', alignRight: false },
  { id: 'created', label: 'DOB / DOI', alignRight: false },
  { id: 'active', label: 'STATUS', alignRight: false },
  // { id: 'onboardedOn', label: 'ONBOARDED ON', alignRight: false },
  { id: 'action', label: 'ACTION', alignRight: false }
];

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9'
  }
}));

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

export default function OnBoarding() {
  const pageCode = 'ONBOARDING_PAGE';
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageMenu, setPageMenu] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [isPartnerTypeModalOpen, setIsPartnerTypeModalOpen] = useState(false);

  const [filterState, setFilterState] = useState({
    typeId: null,
    partnerStatusTypeId: null,
    cityId: null,
    pageSize: 10,
    pageNumber: 1,
    sortOrder: 'desc'
  });

  const [hasNextPage, setHasNextPage] = useState(true);
  const { OnboardingListData } = useOnboardingSelector();
  const { LoginData } = useLoginSelector();
  const debouncedSearch = useDebounce(filterName, 600);
  const { AccessLevelConfig } = useGlobalSelector();

  const getOnboardingListAPi = useCallback(() => {
    if (!LoginData) return;

    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : '',
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: filterState?.sortOrder || 'desc'
      },
      filterModel: {
        typeId: filterState?.typeId ?? null,
        partnerStatusTypeId: filterState?.partnerStatusTypeId ?? null,
        cityId: filterState?.cityId ?? null
      }
    };

    dispatch(getOnboardingsList(data));
  }, [LoginData, debouncedSearch, filterState, dispatch, page, rowsPerPage]);

  useEffect(() => {
    getOnboardingListAPi();
  }, [getOnboardingListAPi]);

  useEffect(() => {
    if (!OnboardingListData) {
      setHasNextPage(true);
      return;
    }
    setHasNextPage(OnboardingListData.length >= rowsPerPage);
  }, [OnboardingListData, rowsPerPage]);

  useEffect(() => {
    if (AccessLevelConfig) {
      AccessLevelConfig?.accessPagesMapping?.forEach((accessPageMap) => {
        if (accessPageMap?.pageCode === pageCode) {
          setPageMenu(accessPageMap?.accessSubPagesMappings);
        }
      });
    }
  }, [AccessLevelConfig]);

  const pageData = useMemo(
    () =>
      pageMenu?.reduce((acc, item) => {
        acc[item?.subPageCode] = item?.isEnable;
        return acc;
      }, {}),
    [pageMenu]
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filterState.typeId, filterState.partnerStatusTypeId, filterState.cityId]);

  useEffect(() => {
    if (Array.isArray(OnboardingListData)) {
      setStatusOptions((prev) => {
        const map = new Map();
        prev.forEach((opt) => {
          map.set(opt.value, opt);
        });
        OnboardingListData.forEach((partner) => {
          const status = partner?.partnerStatus;
          const value = status?.typeId;
          const label = status?.typeName;

          if (value && label && !map.has(value)) {
            map.set(value, { value, label });
          }
        });
        return Array.from(map.values());
      });
    }
  }, [OnboardingListData]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = OnboardingListData?.map((n) => n);
      setSelected(newSelecteds || []);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, partner) => {
    const selectedIndex = selected.indexOf(partner);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, partner);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const UpdatePartnerStatusApi = async (rowId, partner) => {
    const PartnerStatusTypesApi = await PartnerStatusTypes(LoginData?.jwt);
    if (PartnerStatusTypesApi.status === 'success') {
      let partnerStatusTypeId = '';
      PartnerStatusTypesApi.data.partnerStatusTypes.forEach((item) => {
        if (item.typeName === 'Activated') {
          partnerStatusTypeId = item.typeId;
        }
      });

      const statusCredential = {
        companyId: partner.companyId,
        partnerId: rowId,
        partnerStatusTypeId: partnerStatusTypeId
      };
      const UpdatePartnerStatusResponse = await UpdatePartnerStatus(
        statusCredential,
        LoginData?.jwt
      );
      if (UpdatePartnerStatusResponse.status === 'success') {
        getOnboardingListAPi();
      }
    }
  };

  // Total count for pagination
  const totalCount = Array.isArray(OnboardingListData) ? OnboardingListData.length : 0;

  return (
    <Page title="Partner Management System || Partner Onboarding">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Breadcrumb Navigation */}
        <Box sx={{ mb: 1 }}>
          <BrudCrumbs />
        </Box>

        {/* Top Header Row */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#0f172a',
                fontSize: { xs: '22px', sm: '26px' },
                letterSpacing: '-0.4px',
                mb: 0.5
              }}
            >
              On Boarding
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13.5px' }}>
              Onboard and manage your partner network. Track status, verify documents and activate
              partners.
            </Typography>
          </Box>

          {pageData?.ADD_NEW && (
            <Button
              variant="contained"
              onClick={() => setIsPartnerTypeModalOpen(true)}
              startIcon={<AddIcon />}
              sx={{
                bgcolor: '#0284c7',
                color: '#ffffff',
                borderRadius: '10px',
                px: 2.5,
                py: 1.1,
                fontWeight: 600,
                fontSize: '13.5px',
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(2, 132, 199, 0.25)',
                '&:hover': {
                  bgcolor: '#0369a1'
                }
              }}
            >
              Add Partner
            </Button>
          )}
        </Stack>

        {/* Main Table Card */}
        <Card
          sx={{
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <OnBoardingToolbar
            parent="onboarding"
            pageMenu={pageMenu}
            filterName={filterName}
            setFilterName={setFilterName}
            filterState={filterState}
            setFilterState={setFilterState}
            statusOptions={statusOptions}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isPartnerTypeModalOpen={isPartnerTypeModalOpen}
            setIsPartnerTypeModalOpen={setIsPartnerTypeModalOpen}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 960, minHeight: 380 }}>
              <Table>
                <thead>
                  <TableRow>
                    <StyledHeadCell padding="checkbox" sx={{ pl: 2.5 }}>
                      <Checkbox
                        indeterminate={
                          selected.length > 0 && selected.length < (OnboardingListData?.length || 0)
                        }
                        checked={
                          OnboardingListData?.length > 0 &&
                          selected.length === OnboardingListData?.length
                        }
                        onChange={handleSelectAllClick}
                        sx={{
                          color: '#cbd5e1',
                          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                        }}
                      />
                    </StyledHeadCell>
                    {TABLE_HEAD.map((headCell) => (
                      <StyledHeadCell
                        key={headCell.id}
                        align={
                          headCell.id === 'action'
                            ? 'center'
                            : headCell.alignRight
                            ? 'right'
                            : 'left'
                        }
                        sx={
                          headCell.id === 'action'
                            ? { textAlign: 'center' }
                            : headCell.alignRight
                            ? { pr: 3 }
                            : {}
                        }
                      >
                        {headCell.label}
                      </StyledHeadCell>
                    ))}
                  </TableRow>
                </thead>
                <TableBody>
                  {Array.isArray(OnboardingListData) &&
                    OnboardingListData.map((partner, index) => {
                      const isItemSelected = selected.indexOf(partner) !== -1;
                      const partnerTypeName = partner?.partnerType?.typeName;
                      const isIndividual = partnerTypeName?.toLowerCase() === 'individual';
                      const statusName = partner?.partnerStatus?.typeName || 'No status';
                      const statusLower = statusName.toLowerCase();

                      return (
                        <TableRow
                          hover
                          key={partner.partnerId || index}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          sx={{
                            '&:hover': { backgroundColor: 'rgba(2, 132, 199, 0.03)' },
                            '&.Mui-selected, &.Mui-selected:hover': {
                              backgroundColor: 'rgba(2, 132, 199, 0.06)'
                            }
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ pl: 2.5 }}>
                            <Checkbox
                              checked={isItemSelected}
                              onChange={(event) => handleClick(event, partner)}
                              sx={{
                                color: '#cbd5e1',
                                '&.Mui-checked': { color: '#0284c7' }
                              }}
                            />
                          </TableCell>

                          {/* Partner ID */}
                          <StyledTableCell sx={{ fontWeight: 600, color: '#475569' }}>
                            {partner.partnerState === null ||
                            partner?.partnerStatus?.typeName === 'Rejected' ||
                            partner?.partnerStatus?.typeName === 'Approved' ||
                            partner?.partnerStatus?.typeName === 'Inactive' ||
                            partner?.partnerStatus?.typeName === 'Incomplete'
                              ? ''
                              : partner?.partnerIdToDisplay}
                          </StyledTableCell>

                          {/* Partner Name */}
                          <StyledTableCell sx={{ fontWeight: 600, color: '#0f172a' }}>
                            {partner?.partnerName || partner?.companyName || NOT_AVAILABLE}
                          </StyledTableCell>

                          {/* Partner Type Pill */}
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.35,
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                bgcolor: isIndividual ? '#e0f2fe' : '#f3e8ff',
                                color: isIndividual ? '#0284c7' : '#9333ea'
                              }}
                            >
                              {partnerTypeName}
                            </Box>
                          </StyledTableCell>

                          {/* Location */}
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: '#475569',
                                fontSize: '13px'
                              }}
                            >
                              <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                              {partner?.cityEntity?.cityName || partner?.city || NOT_AVAILABLE}
                            </Box>
                          </StyledTableCell>

                          {/* Mobile */}
                          <StyledTableCell sx={{ color: '#334155' }}>
                            {partner?.mobile || partner?.phoneNumber || NOT_AVAILABLE}
                          </StyledTableCell>

                          {/* DOB / DOI */}
                          <StyledTableCell sx={{ color: '#64748b' }}>
                            {isIndividual
                              ? partner?.dob || '-'
                              : partner?.dateOfIncorporation || '-'}
                          </StyledTableCell>

                          {/* Status Pill */}
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.35,
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                ...(statusLower === 'activated'
                                  ? { bgcolor: '#dcfce7', color: '#16a34a' }
                                  : statusLower === 'inactive'
                                  ? { bgcolor: '#f1f5f9', color: '#475569' }
                                  : statusLower === 'rejected'
                                  ? { bgcolor: '#fee2e2', color: '#dc2626' }
                                  : { bgcolor: '#fef3c7', color: '#d97706' })
                              }}
                            >
                              {statusName}
                            </Box>
                          </StyledTableCell>

                          {/* Onboarded On */}
                          {/* <StyledTableCell sx={{ color: '#64748b', fontSize: '12.5px' }}>
                            {formatDate(
                              partner?.created || partner?.createdAt || partner?.creationDate
                            )}
                          </StyledTableCell> */}

                          {/* Action Menu */}
                          <StyledTableCell align="center">
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <UserMoreMenu
                                partner={partner}
                                rowId={partner.partnerId}
                                parent="on-boarding"
                                UpdatePartnerStatusApi={UpdatePartnerStatusApi}
                                pageMenu={pageMenu}
                                partnerState={partner.partnerStatus}
                              />
                            </Stack>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>

                {(!OnboardingListData || OnboardingListData.length === 0) && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={TABLE_HEAD.length + 1} sx={{ py: 6 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Pagination Footer matching the design */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            {/* Left: Range text */}
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
              Showing {totalCount > 0 ? page * rowsPerPage + 1 : 0} to{' '}
              {totalCount > 0 ? page * rowsPerPage + totalCount : 0} partners
            </Typography>

            {/* Right: Rows per page + Page Buttons */}
            <Stack direction="row" alignItems="center" spacing={2.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Rows per page</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    fontSize: '13px',
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Stack>

              {/* Page Numbers Navigation */}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.5,
                    color: '#64748b',
                    '&:disabled': { opacity: 0.4 }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    px: 1,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 700,
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {page + 1}
                </Box>

                <IconButton
                  size="small"
                  disabled={!hasNextPage}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.5,
                    color: '#64748b',
                    '&:disabled': { opacity: 0.4 }
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Card>
      </Container>
    </Page>
  );
}
